'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import { LandingPlanCard } from './LandingPlanCard';
import { PLCTA } from './atoms';
import { useHandlePerformanceLabBuyNavigate } from './hooks';

import {
  FEATURED_PLAN_ID,
  LANDING_DIAGNOSTIC_PLANS,
  LANDING_RECOVERY_PLANS,
  RECOVERY_FEATURED_PLAN_ID,
  type PlansSectionVariant,
} from './plansSectionData';
import {
  PlContentShellCentered,
  PlSectionHeader,
  PlSectionHeading,
  PlSectionLabel,
  PlSectionRoot,
  PlTitleStack,
  SectionContent,
} from './sectionLayout';
import styles from './PlansSection.module.css';

interface PlansSectionProps {
  variant?: PlansSectionVariant;
}

export const PlansSection: React.FC<PlansSectionProps> = ({
  variant = 'diagnostics',
}) => {
  const t = useTranslations('performance-lab');
  const isRecovery = variant === 'recovery';
  const plans = isRecovery ? LANDING_RECOVERY_PLANS : LANDING_DIAGNOSTIC_PLANS;
  const featuredPlanId = isRecovery
    ? RECOVERY_FEATURED_PLAN_ID
    : FEATURED_PLAN_ID;
  const i18nPrefix = isRecovery ? 'recoveryPage.plansSection' : 'plansSection';

  const handleNavigateToBuy = useHandlePerformanceLabBuyNavigate();
  const onClickCta = () =>
    handleNavigateToBuy({ category: isRecovery ? 'recovery' : 'diagnostics' });

  return (
    <PlSectionRoot
      id={isRecovery ? 'recovery-plans-section' : undefined}
      aria-label={t(`${i18nPrefix}.aria`)}
    >
      <SectionContent>
        <PlContentShellCentered className={styles.shell}>
          <PlSectionHeader>
            <PlSectionLabel>
              {t(`${i18nPrefix}.label`)}
            </PlSectionLabel>
            <PlTitleStack>
              <PlSectionHeading $align="center">
                {t(`${i18nPrefix}.title`)}
              </PlSectionHeading>
            </PlTitleStack>
            <PLCTA
              type="button"
              $plVariant="primary"
              onClick={onClickCta}
              aria-label={
                t.has(`${i18nPrefix}.viewPlansAria`)
                  ? t(`${i18nPrefix}.viewPlansAria`)
                  : 'View performance lab plans'
              }
              data-buttontype="performance_lab_plans_section_view_plans"
            >
              {t.has(`${i18nPrefix}.viewPlans`)
                ? t(`${i18nPrefix}.viewPlans`)
                : 'View plans'}
            </PLCTA>
          </PlSectionHeader>

          <div className={styles.cardsTrack}>
            <div className={styles.desktopGrid}>
              {plans.map((plan) => (
                <LandingPlanCard
                  key={plan.id}
                  plan={plan}
                  featured={plan.id === featuredPlanId}
                  variant={variant}
                />
              ))}
            </div>

            <div
              className={styles.mobileStack}
              role="region"
              aria-label={t(`${i18nPrefix}.mobileListAria`)}
            >
              {plans.map((plan) => (
                <LandingPlanCard
                  key={plan.id}
                  plan={plan}
                  featured={plan.id === featuredPlanId}
                  variant={variant}
                />
              ))}
            </div>
          </div>
        </PlContentShellCentered>
      </SectionContent>
    </PlSectionRoot>
  );
};
